import React, {
  ComponentProps,
  FC,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { BoxV2 as Box, Flex, useHasPermission } from 'portal-commons';
import SectionCard from './SectionCard';
import FieldBlock from './FieldBlock';
import EmailEditModal, { EmailEditPayload } from './EmailEditModal';
import {
  faIndustryWindows,
  faMessageDots,
  faGearCode,
} from '@fortawesome/pro-regular-svg-icons';
import { CspProfile } from '@/types/user';
import { emailArrayToString } from '../../../CspRegistration/helpers';
import { profileController } from '@/apis/core';
import { EmailField } from '../../../CspRegistration/types';
import { toastFlashMessage } from '@/utils';

interface Props {
  profile: CspProfile;
  onUpdate: (updated: CspProfile) => void;
}

enum EmailType {
  CommercialContacts = 'commercial',
  SystemNotifications = 'system',
  TechnicalContacts = 'technical',
}

const EMAIL_SETTINGS: Record<
  EmailType,
  {
    title: string;
    description: string;
    payloadKey: 'email' | 'escalateEmail' | 'techEmail';
  }
> = {
  [EmailType.CommercialContacts]: {
    title: 'Commercial Contacts',
    description: 'Used for contract and business communications',
    payloadKey: 'email',
  },
  [EmailType.SystemNotifications]: {
    title: 'System Notifications',
    description: 'Used for system event notifications',
    payloadKey: 'escalateEmail',
  },
  [EmailType.TechnicalContacts]: {
    title: 'Technical Contacts',
    description:
      'Complete this using your support team email, it will be used if other emails given do not respond.',
    payloadKey: 'techEmail',
  },
};
const TwoColumnGrid: FC<ComponentProps<typeof Box>> = ({
  children,
  sx,
  ...rest
}) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        rowGap: '16px',
        columnGap: '16px',
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const TabEmailPreferences: FC<Props> = ({ profile, onUpdate }) => {
  // Check if the user has permission to edit email
  const authorized = useHasPermission('profile.editEmail') as boolean;
  // Use EmailType | null to represent the modal state directly
  const [modal, setModal] = useState<EmailType | null>(null);
  const [updating, setUpdating] = useState(false);

  const splitEmail = useCallback((str: string): string[] => {
    return (
      str
        .split(',')
        .filter(Boolean)
        .map((email) => email.trim()) || []
    );
  }, []);

  const commercialEmails = useMemo(
    () => splitEmail(profile.email),
    [profile.email, splitEmail]
  );
  const systemEmails = useMemo(
    () => splitEmail(profile.escalateEmail),
    [profile.escalateEmail, splitEmail]
  );
  const technicalEmails = useMemo(
    () => splitEmail(profile.techEmail),
    [profile.techEmail, splitEmail]
  );

  const updateEmail = async (data: EmailEditPayload) => {
    if (!modal) return;

    setUpdating(true);
    const updatedEmail = emailArrayToString(data.emails as EmailField[]);

    const payload = {
      [EMAIL_SETTINGS[modal].payloadKey]: updatedEmail,
    };

    try {
      const response = await profileController.updateProfileEmail(payload);
      if (response) {
        toastFlashMessage('Email Preferences Updated', 'success');
        onUpdate(response); // Trigger parent to refetch data
        setModal(null); // Close modal on success
      }
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Flex
      sx={{ columnGap: 24, mt: 10 }}
      data-testid="tabEmailPreferencesContainer"
    >
      {/* Commercial Contacts */}
      <SectionCard
        icon={faIndustryWindows}
        title="Commercial Contacts"
        editable={authorized}
        onEdit={() => {
          authorized && setModal(EmailType.CommercialContacts);
        }}
        dataTestId="commercialContactsSection"
      >
        <Box>Used for contract and business communications.</Box>
        <TwoColumnGrid sx={{ mt: 16 }}>
          {commercialEmails.length > 0 ? (
            commercialEmails.map((email, index) => (
              <FieldBlock
                key={`commercialEmail_${index}`}
                label="Email Address"
                value={email}
                dataTestId={`commercialEmail_${index}`}
              />
            ))
          ) : (
            <FieldBlock
              label="Email Address"
              value="N/A"
              dataTestId="commercialEmailEmpty"
            />
          )}
        </TwoColumnGrid>
      </SectionCard>

      {/* System Notifications */}
      <SectionCard
        icon={faMessageDots}
        title="System Notifications"
        editable={authorized}
        onEdit={() => {
          authorized && setModal(EmailType.SystemNotifications);
        }}
        dataTestId="systemNotificationsSection"
      >
        <Box>Used for system event notification.</Box>
        <TwoColumnGrid sx={{ mt: 16 }}>
          {systemEmails.length > 0 ? (
            systemEmails.map((email, index) => (
              <FieldBlock
                key={`systemEmail_${index}`}
                label="Email Address"
                value={email}
                dataTestId={`systemEmail_${index}`}
              />
            ))
          ) : (
            <FieldBlock
              label="Email Address"
              value="N/A"
              dataTestId="systemEmailEmpty"
            />
          )}
        </TwoColumnGrid>
      </SectionCard>

      {/* Technical Contacts */}
      <SectionCard
        icon={faGearCode}
        title="Technical Contacts"
        editable={authorized}
        onEdit={() => {
          authorized && setModal(EmailType.TechnicalContacts);
        }}
        dataTestId="technicalContactsSection"
      >
        <Box>
          Used to communicate with your support team. Also serves as a fallback
          if other provided addresses do not respond to contact.
        </Box>
        <TwoColumnGrid sx={{ mt: 16 }}>
          {technicalEmails.length > 0 ? (
            technicalEmails.map((email, index) => (
              <FieldBlock
                key={`technicalEmail_${index}`}
                label="Email Address"
                value={email}
                dataTestId={`technicalEmail_${index}`}
              />
            ))
          ) : (
            <FieldBlock
              label="Email Address"
              value="N/A"
              dataTestId="technicalEmailEmpty"
            />
          )}
        </TwoColumnGrid>
      </SectionCard>

      {modal && (
        <EmailEditModal
          open={!!modal}
          disabled={updating} // Disable form while saving
          title={EMAIL_SETTINGS[modal].title}
          description={EMAIL_SETTINGS[modal].description}
          emails={
            modal === EmailType.CommercialContacts
              ? commercialEmails
              : modal === EmailType.SystemNotifications
              ? systemEmails
              : technicalEmails
          }
          onClose={() => setModal(null)}
          onSubmit={updateEmail}
        />
      )}
    </Flex>
  );
};

export default TabEmailPreferences;
